import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AuthContext = createContext(null);

const ROLE_LABELS = {
  system_admin: "SDTD Administrator",
  admin: "SDTD Monitor",
  spoc: "SPOC",
  management: "Management (view only)",
  pravartanam_partner: "Pravartanam Partner",
  pravartanam_manager: "Pravartanam Partner — Manager",
  viewer: "Viewer",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(
    () =>
      api
        .get("/auth/me")
        .then((r) => setUser(r.data))
        .catch(() => setToken(null)),
    [],
  );

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    fetchMe().finally(() => setLoading(false));
  }, [fetchMe]);

  const login = (token, u) => {
    setToken(token);
    setUser(u);
  };
  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const isAdmin = ["system_admin", "admin"].includes(user?.role);
  const isPartner = ["pravartanam_partner", "pravartanam_manager"].includes(user?.role);
  const isPartnerManager = user?.role === "pravartanam_manager";
  const isManagement = user?.role === "management";
  const isViewer = user?.role === "viewer";
  const isDesignated = user?.role === "spoc_member";
  const readOnly = ["management", "viewer"].includes(user?.role);
  const canEdit = isAdmin || ["spoc", "spoc_member"].includes(user?.role) || isPartner;
  // Approving a unit's data is the unit SPOC's own call; a partner's work is the Manager's.
  const canVerify =
    user?.role === "spoc" || isPartnerManager || user?.super_admin_mode === true;
  const superAdmin = user?.super_admin_mode === true;
  const superAdminEligible = user?.super_admin_eligible === true;
  const seesQueue = isAdmin || user?.role === "spoc" || isPartner;
  // A SPOC reads every unit's work but edits only their own; the trail is SDTD + SPOC only.
  const canReadAll = isAdmin || user?.role === "spoc";
  const canSeeActivity = isAdmin || user?.role === "spoc";
  const scopedPlant = ["spoc", "spoc_member"].includes(user?.role) ? user?.plant || null : null;
  // Which screens this role may reach — the portal itself says so, in auth.ROLE_SCREENS, as the
  // control cockpit has left it.
  const screens = user?.screens || [];
  const sees = (screen) => !screen || screens.includes(screen);
  // Blocks the cockpit has switched off for this account, and screens it may only read.
  const hiddenSections = user?.hidden_sections || [];
  const readOnlyScreens = user?.read_only_screens || [];
  const shows = (key) => !key || !hiddenSections.includes(key);
  const writable = (screen) => !screen || !readOnlyScreens.includes(screen);
  // Tabs SDTD has put behind a "coming soon" curtain for this officer, and what each one says.
  const curtains = user?.curtains || {};
  const curtained = (screen) => Boolean(screen && curtains[screen]);
  const cockpitOwner = user?.cockpit_owner === true;
  const profileComplete = user ? user.profile_complete !== false : true;
  const passwordChangeRequired = user?.password_change_required === true;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        refresh: fetchMe,
        isAdmin,
        isPartner,
        isPartnerManager,
        isManagement,
        isViewer,
        isDesignated,
        canVerify,
        seesQueue,
        superAdmin,
        superAdminEligible,
        canReadAll,
        canSeeActivity,
        screens,
        sees,
        shows,
        writable,
        hiddenSections,
        readOnlyScreens,
        curtains,
        curtained,
        cockpitOwner,
        canExportReports: !isViewer,
        readOnly,
        canEdit,
        canUpload: isAdmin || user?.role === "spoc" || isPartner,
        profileComplete,
        passwordChangeRequired,
        scopedPlant,
        roleLabel: ROLE_LABELS[user?.role] || "",
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
